import { Button, Kind } from '@thanx/uikit/button'
import { Notification } from '@thanx/uikit/notification'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import { locationsApi } from 'api/locations'
import palette from 'constants/palette'
import useCurrentMerchant from 'hooks/useCurrentMerchant'
import { useOrderingAccess } from 'hooks/useOrderingAccess'
import { buildTranslate } from 'locales'
import type { Fields as Location } from 'models/Location'
import React, { useMemo, useState } from 'react'
import { Container } from 'react-bootstrap-five'
import Helmet from 'react-helmet'
import { Link, type RouteComponentProps } from 'react-router-dom'
import { useDisplayError } from 'utilities/displayError'
import { formatAddressLine } from '../addressUtils'
import HelpLinks from '../HelpLinks'
import LocationDetailsModal from '../LocationDetailsModal'
import { toConsolidatedLocation } from '../normalisers'
import { formatLastUpdated, toIssues, toMenu } from './helpers'
import IssuePanel from './IssuePanel'
import MenuErrorsCrossLink from './MenuErrorsCrossLink'
import MenuList from './MenuList'
import OrderingStatusBadge from './OrderingStatusBadge'
import ScheduleOverrides from './ScheduleOverrides'
import ServiceCharges from './ServiceCharges'
import type { Params } from './types'

const t = buildTranslate('locations.location_list')

// LocationDetail mounts at /locations/:locationId. Renders a custom
// header surface (back link, status badge, location name + address,
// toggle CTA) above the body sections. Importantly, the surrounding
// element is a <div> rather than a <header>: the global stylesheet at
// src/index.scss applies `position: fixed` to every <header> on the
// page (matching the app navbar pattern), which would otherwise pin
// this whole block to the top of the viewport and collapse it to the
// navbar's height. Falling back to a "not found" message when the id
// doesn't match keeps the route working when a stale link points at a
// deleted location.
const LocationDetail = ({ match }: RouteComponentProps<Params>) => {
  const [css] = useStyletron()
  const numericId = Number(match.params.locationId)
  // Skip the queries entirely if the URL segment didn't parse —
  // otherwise `Number('abc')` → NaN and we'd send a real GET to
  // `locations/NaN/ordering_config`.
  const hasValidId = Number.isInteger(numericId) && numericId > 0
  // Non-Toast merchants can't toggle ordering on a per-location basis —
  // hide the badge + CTA per Figma 432:2072 "Non-Toast merchant will not
  // have" note. The Detail page remains useful as a read-only view.
  const { supportsOrderingManagement: isToastMerchant } = useOrderingAccess()
  // Names the issue-banner source label ("Error from Toast") instead of
  // the generic "ordering provider" copy — Figma 5:1028 / DATA-5333.
  const merchant = useCurrentMerchant()

  const {
    data: configData,
    isLoading: configLoading,
    isError: configError,
    refetch: refetchConfig,
  } = locationsApi.useGetOrderingConfigQuery(
    { locationId: numericId },
    { skip: !hasValidId }
  )
  const { data: menusData } = locationsApi.useGetLocationMenusQuery(
    { locationId: numericId },
    { skip: !hasValidId }
  )
  const [bulkUpdate, { isLoading: toggling }] =
    locationsApi.useBulkUpdateOrderingStatusMutation()
  const displayError = useDisplayError()
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false)
  // Joined reasons the last enable was refused (200 + enable_failures), or null.
  // Shown as an inline notice in the page body rather than a global flash — the
  // flash pins to the fixed app <header> and reads as a page-level error, while
  // the list screen surfaces the same failure inline. Keep the two consistent.
  const [enableFailure, setEnableFailure] = useState<string | null>(null)
  // The Details modal needs phone / time_zone / External ID —
  // OrderingConfigEntity doesn't carry them, so we read the location's
  // detail representation (GET locations/:id, which the LocationEntity
  // presents). Lazy-fetched: skip until the modal actually opens so a
  // page-view doesn't fetch it for an operator who never opens Details.
  // A single-row detail fetch (vs. the /locations list) keeps this cheap
  // and is the only representation that carries External ID — the field
  // is excluded from the minimal list response. RTK Query caches the
  // response so subsequent opens are instant.
  const { data: fullLocationData } = locationsApi.useGetLocationQuery(
    { locationId: numericId },
    { skip: !isDetailsModalOpen || !hasValidId }
  )
  const fullLocation: Location | undefined = fullLocationData?.location

  const handleToggleOrdering = async () => {
    if (!location) return
    // Clear any prior refusal notice at the start of a new toggle.
    setEnableFailure(null)
    try {
      const result = await bulkUpdate({
        action: location.orderingEnabled ? 'disable' : 'enable',
        locationIds: [location.id],
      }).unwrap()
      // 200 with a skip: the BE couldn't enable this location (e.g. delivery is
      // on with no handler). Surface the reason inline instead of a silent no-op.
      const failure = result.enable_failures?.[0]
      if (!location.orderingEnabled && failure) {
        setEnableFailure(failure.reasons.join('; '))
      }
    } catch (e) {
      displayError(e, t('location_detail.toggle_failed'))
    }
  }

  const location = useMemo(
    () =>
      configData
        ? toConsolidatedLocation(configData.ordering_config)
        : undefined,
    [configData]
  )
  const issues = useMemo(
    () =>
      toIssues(
        configData?.ordering_config.config_errors ?? [],
        merchant?.ordering_provider
      ),
    [configData, merchant]
  )
  const enabledMenus = useMemo(
    () => (menusData?.menus ?? []).filter(m => m.enabled).map(toMenu),
    [menusData]
  )
  const disabledMenus = useMemo(
    () => (menusData?.menus ?? []).filter(m => !m.enabled).map(toMenu),
    [menusData]
  )
  const lastUpdated = formatLastUpdated(
    configData?.ordering_config.location_last_synced_at ?? null,
    fullLocation?.time_zone
  )

  if (!configLoading && (configError || !location)) {
    return (
      <div
        className={css({
          backgroundColor: palette.grey05,
          minHeight: '100%',
        })}
      >
        <Helmet>
          <title>{t('page_title')}</title>
        </Helmet>
        <Container className="px-0 py-l">
          <Link
            to="/locations"
            className={`d-inline-flex align-items-center bold body-text-3 ${css(
              {
                color: palette.grey90,
              }
            )}`}
          >
            <i className="fa fa-arrow-left mr-xs" aria-hidden="true" />
            {t('location_detail.back')}
          </Link>
          <p className={`mt-l ${css({ color: palette.grey70 })}`}>
            {t('location_detail.not_found')}
          </p>
        </Container>
      </div>
    )
  }

  if (!location) {
    return (
      <div
        className={css({
          backgroundColor: palette.grey05,
          minHeight: '100%',
        })}
      >
        <Container className="px-0 py-l">
          <p
            className={`body-text-4 mb-0 ${css({ color: palette.grey70 })}`}
            role="status"
          >
            {t('location_detail.loading')}
          </p>
        </Container>
      </div>
    )
  }

  return (
    <div
      className={css({ backgroundColor: palette.grey05, minHeight: '100%' })}
    >
      <Helmet>
        <title>
          {t('location_detail.page_title', { name: location.name })}
        </title>
      </Helmet>
      <div
        className={`pt-l pb-l ${css({
          backgroundColor: palette.white,
          borderBottom: `1px solid ${palette.grey20}`,
        })}`}
      >
        <Container className="px-0 pb-xl">
          <Link
            to="/locations"
            className={`d-inline-flex align-items-center mb-m bold body-text-3 ${css(
              {
                color: palette.grey90,
              }
            )}`}
          >
            <i className="fa fa-arrow-left mr-xs" aria-hidden="true" />
            {t('location_detail.back')}
          </Link>
          <div className="d-flex align-items-center justify-content-between">
            <div>
              {isToastMerchant && (
                <div className="mb-s">
                  <OrderingStatusBadge enabled={location.orderingEnabled} />
                </div>
              )}
              <Text.Header2 className="m-0">{location.name}</Text.Header2>
              <p
                className={`mt-xs mb-0 body-text-3 ${css({
                  color: palette.grey90,
                })}`}
              >
                {formatAddressLine(location)}
              </p>
            </div>
            <div className="d-flex" style={{ gap: '12px' }}>
              <Button
                kind={Kind.SECONDARY}
                onClick={() => setIsDetailsModalOpen(true)}
              >
                {t('location_detail.view_details')}
              </Button>
              {isToastMerchant && (
                <Button
                  kind={Kind.PRIMARY}
                  disabled={toggling || !location.orderingConfigured}
                  onClick={handleToggleOrdering}
                >
                  {location.orderingEnabled
                    ? t('location_detail.cta_disable')
                    : t('location_detail.cta_enable')}
                </Button>
              )}
            </div>
          </div>
        </Container>
      </div>
      <Container className="px-0 py-l">
        {enableFailure && (
          <div className="mb-l">
            <Notification
              kind="negative"
              size="full"
              closeable
              onClose={() => setEnableFailure(null)}
            >
              {t('location_detail.enable_failed', { reasons: enableFailure })}
            </Notification>
          </div>
        )}
        <div className="d-flex justify-content-end mb-l">
          <p
            className={`body-text-4 mb-0 ${css({ color: palette.grey70 })}`}
            data-testid="last-updated"
          >
            {lastUpdated
              ? t('location_detail.last_updated', { timestamp: lastUpdated })
              : t('location_detail.not_yet_synced')}{' '}
            <button
              type="button"
              onClick={() => refetchConfig()}
              className={css({
                background: 'none',
                border: 'none',
                padding: 0,
                color: palette.belizeHole,
                cursor: 'pointer',
              })}
            >
              {t('location_detail.update')}
            </button>
          </p>
        </div>
        {issues.length > 0 && (
          <section className="mb-xl">
            {issues.map(issue => (
              <IssuePanel key={issue.id} issue={issue} />
            ))}
          </section>
        )}
        {(configData?.ordering_config.menu_error_count ?? 0) > 0 && (
          <MenuErrorsCrossLink />
        )}
        <ScheduleOverrides
          overrides={configData?.ordering_config.schedule_overrides ?? []}
        />
        <ServiceCharges locationId={numericId} />
        <MenuList
          title={t('location_detail.menus_enabled.title')}
          description={t('location_detail.menus_enabled.description')}
          menus={enabledMenus}
        />
        <MenuList
          title={t('location_detail.menus_disabled.title')}
          description={t('location_detail.menus_disabled.description')}
          menus={disabledMenus}
        />
        <HelpLinks />
      </Container>
      {fullLocation && isDetailsModalOpen && (
        <LocationDetailsModal
          isOpen
          location={fullLocation}
          onClose={() => setIsDetailsModalOpen(false)}
        />
      )}
    </div>
  )
}

export default LocationDetail
